/**
 * responseVariationEngine.js — Re Leve IA (Etapa 5)
 * Evita respostas repetitivas usando variações seguras e determinísticas.
 * Usa hash do customerId + intent + variantKey para seleção sem Math.random.
 * Guardrails sensíveis têm variação mínima, sem alterar conteúdo de segurança.
 */

// ── Hash determinístico (djb2) ─────────────────────────────────────────────────

function djb2Hash(str) {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) ^ str.charCodeAt(i);
  }
  return Math.abs(hash);
}

function selectVariant(variants, customerId, intent, variantKey) {
  const seed = `${customerId || 'anonimo'}_${intent}_${variantKey || 'default'}`;
  const index = djb2Hash(seed) % variants.length;
  return variants[index];
}

// ── Variações por intenção ─────────────────────────────────────────────────────

const VARIANTS = {
  saudacao: [
    'Olá! Sou o assistente Re Leve. Posso te ajudar a montar um kit para hoje, organizar sua semana ou registrar seu consumo de forma informativa. 🌿',
    'Oi! Que bom ter você aqui! Posso montar um kit para o seu dia, organizar sua semana ou registrar suas refeições Re Leve. 🌿',
    'Olá! Estou aqui para ajudar com kits diários, semanas organizadas e acompanhamento informativo de refeições. Por onde prefere começar? 🌿',
  ],
  saudacao_fiel: [
    'Olá! Que bom te ver de novo! Posso montar sua rotina habitual ou prefere ver novidades? 🌿',
    'Que saudade! Posso organizar sua semana habitual ou explorar algo diferente desta vez? 🌿',
    'Olá! Seja bem-vindo de volta! Sua rotina está pronta para eu remontar ou quer algo novo? 🌿',
  ],
  saudacao_novo: [
    'Olá! Seja bem-vindo à Re Leve! Posso te mostrar como montar kits para o dia ou para a semana. Por onde quer começar? 🌿',
    'Olá! Bem-vindo! Aqui na Re Leve você resolve a alimentação da semana com facilidade. Quer começar por um kit do dia? 🌿',
    'Oi! Seja muito bem-vindo! Posso mostrar nossos kits ou organizar uma semana completa. O que prefere? 🌿',
  ],
  montar_semana: [
    'Posso montar uma semana mais prática para você, combinando refeições e complementos em um kit.',
    'Dá para organizar sua rotina com um kit semanal e deixar a recompra mais simples.',
    'Podemos transformar sua compra em uma semana planejada, com refeições e complementos.',
  ],
  kit_dia: [
    'Posso te ajudar com um Kit Dia Leve — almoço, jantar leve e snack, tudo em um único pedido.',
    'Um Kit Dia Leve resolve o dia inteiro: almoço, jantar e snack. Quer incluir suco natural também?',
    'Para resolver o dia com praticidade: almoço, jantar leve e snack em um só pedido.',
  ],
  promocoes: [
    'Temos algumas oportunidades agora: Semana Resolvida, Recompra Programada, Indique e Ganhe e Kit Escritório Saudável. Qual te interessa mais?',
    'Neste momento temos: Semana Resolvida, Programa de Indicação, Recompra Programada e Kit para equipes. O que chama mais atenção?',
    'Entre nossas promoções: Semana completa com desconto, programa de indicação com benefício e kit para empresas. Por qual quer começar?',
  ],
  indicacao: [
    'Você pode indicar alguém e receber um benefício na próxima compra. Quer que eu gere uma mensagem para você enviar?',
    'Indique um amigo e ganhe na sua próxima compra. Posso preparar uma mensagem de indicação para você compartilhar.',
    'Tem alguém que precisaria conhecer a Re Leve? Indique e ganhe um benefício. Quer que eu monte a mensagem?',
  ],
  whatsapp: [
    'Perfeito! Posso montar um resumo do seu interesse para você finalizar pelo WhatsApp. Quer que eu prepare agora?',
    'Vou preparar um resumo para o WhatsApp. Em segundos você terá tudo organizado para confirmar com a equipe.',
    'Ótimo! Preparo um resumo do seu pedido para finalizar no WhatsApp. Quer seguir?',
  ],
  fallback: [
    'Posso te ajudar com kits para o dia, semana completa, promoções, acompanhamento calórico informativo ou encaminhamento para WhatsApp. O que você prefere?',
    'Estou aqui para ajudar! Quer montar um kit, ver promoções, registrar refeições ou falar no WhatsApp?',
    'Como posso ajudar? Posso montar kits, mostrar promoções, registrar refeições Re Leve de forma informativa ou encaminhar ao WhatsApp.',
  ],
  calorias_ativo: [
    'Seu acompanhamento informativo está ativo. Posso registrar uma refeição Re Leve ou montar um kit para completar seu dia.',
    'Acompanhamento calórico informativo ativado. Quer registrar uma refeição ou criar um plano para o dia?',
    'Ótimo! Seu registro informativo está funcionando. Posso adicionar uma refeição ou sugerir um kit completo.',
  ],
  restricao_salva: [
    'Obrigado por avisar! Vou considerar essa informação na conversa e confirmar as opções disponíveis no atendimento antes do fechamento.',
    'Anotado! Vou levar sua restrição em consideração. As opções compatíveis serão confirmadas no atendimento.',
    'Entendido! Registrei sua restrição. Antes de finalizar, o atendimento confirmará a disponibilidade de opções compatíveis.',
  ],
};

// ── Exportação principal ───────────────────────────────────────────────────────

export function selectResponseVariant({ intent, segment, variantKey, customerId }) {
  const seg = (segment || '').toLowerCase();
  const isFiel = seg.includes('fiel') || seg.includes('alto valor');
  const isNovo = seg.includes('novo');

  let variantGroup = intent;

  // Variação por segmento para saudação
  if (intent === 'saudacao') {
    if (isFiel) variantGroup = 'saudacao_fiel';
    else if (isNovo) variantGroup = 'saudacao_novo';
    else variantGroup = 'saudacao';
  }

  const variants = VARIANTS[variantGroup] || VARIANTS.fallback;
  return selectVariant(variants, customerId, variantGroup, variantKey);
}

export function getCalorieActiveVariant(customerId) {
  return selectVariant(VARIANTS.calorias_ativo, customerId, 'calorias_ativo', 'active');
}

export function getRestrictionSavedVariant(customerId) {
  return selectVariant(VARIANTS.restricao_salva, customerId, 'restricao_salva', 'saved');
}
